/**
 * Product review-related API endpoints
 */
import { apiGet, apiPost } from "./client.js";

/**
 * Fetches product reviews with pagination
 * @param {string} productType - Product type (frames, sunglasses, accessories)
 * @param {string} productId - Product ID
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of reviews per page
 * @returns {Promise<Response>} Product reviews response
 */
export const getProductReviews = (productType, productId, page = 1, pageSize = 5) => {
  if (!productType || !productId) {
    throw new Error("Product type and ID are required for fetching reviews");
  }

  // Map frontend product types to backend types
  const typeMapping = {
    frames: "frame",
    sunglasses: "sunglass",
    accessories: "accessory",
  };

  const backendType = typeMapping[productType];
  if (!backendType) {
    throw new Error(`Invalid product type: ${productType}`);
  }

  return apiGet(`/reviews/product/${backendType}/${productId}`, { page, pageSize });
};

/**
 * Submits a new product review
 * @param {Object} reviewData - Review data including productType, productId, and review content
 * @returns {Promise<Response>} Review submission response
 */
export const submitProductReview = (reviewData) => {
  const { productType, productId, recaptchaToken, ...otherData } = reviewData;

  if (!productType || !productId) {
    throw new Error("Product type and ID are required for submitting reviews");
  }

  // Map product type to the correct relation field
  const relationField = {
    frames: "frame_product",
    sunglasses: "sunglass_product",
    accessories: "accessory_product",
  }[productType];

  if (!relationField) {
    throw new Error(`Invalid product type: ${productType}`);
  }

  const transformedData = {
    ...otherData,
    [relationField]: productId,
  };

  return apiPost("/reviews", { data: transformedData, recaptchaToken: recaptchaToken || null });
};

/**
 * Fetches product review statistics
 * @param {string} productType - Product type (frames, sunglasses, accessories)
 * @param {string} productId - Product ID
 * @returns {Promise<Response>} Review statistics response
 */
export const getProductReviewStats = (productType, productId) => {
  if (!productType || !productId) {
    throw new Error("Product type and ID are required for fetching review stats");
  }

  // Map frontend product types to backend types
  const typeMapping = {
    frames: "frame",
    sunglasses: "sunglass",
    accessories: "accessory",
  };

  const backendType = typeMapping[productType];
  if (!backendType) {
    throw new Error(`Invalid product type: ${productType}`);
  }

  return apiGet(`/reviews/product/${backendType}/${productId}/stats`);
};
